import { API_URL } from "./auth-shared";

/**
 * Remote-config client for the landing page.
 *
 * Mirrors the architecture of {@link ./feature-flags.ts} but for typed
 * scalar/JSON config values (titles, prices, JSON blobs) instead of
 * boolean flags.
 *
 * Fails open: an API outage returns an empty map and consumers fall
 * back to the defaults they pass into the typed getters.
 */

export type ConfigPlatform = "LANDING" | "ANDROID" | "IOS";
export type ConfigValueType = "STRING" | "NUMBER" | "BOOLEAN" | "JSON";

interface EvaluatedConfig {
  key: string;
  type: ConfigValueType;
  value: unknown;
}

/**
 * Map of resolved config values, locale-aware. Use the typed helpers
 * ({@link getConfigString}, {@link getConfigNumber}, etc.) to read
 * entries — they enforce shape and fall back gracefully.
 */
export type ConfigsMap = Record<
  string,
  { type: ConfigValueType; value: unknown }
>;

/**
 * Fetches the evaluated config set for the given platform and locale.
 *
 * Caching: NONE. We pass `cache: "no-store"` so every landing request
 * pulls fresh values from the API. The previous design relied on
 * `revalidatePath('/', 'layout')` from the panel BFF to invalidate a
 * 1-hour revalidate window — that was incorrect: panel and landing
 * are separate Next.js apps in different containers, so the panel's
 * cache invalidation never reaches the landing's cache. Coverage
 * edits (and any other remote-config / feature-flag change) would
 * sit invisible for up to 1 hour on production.
 *
 * Trade-off: every landing visit costs one extra API call. Acceptable
 * during pilot (sub-100 visits/day) and the API endpoint is cheap.
 * If traffic grows materially we should add a cross-app revalidation
 * webhook (landing exposes `/api/revalidate` with a shared secret,
 * panel BFFs call it after mutations) and re-introduce a short
 * revalidate window.
 *
 * Fails open with `{}` on any error.
 */
export async function fetchRemoteConfig(
  platform: ConfigPlatform,
  locale?: string,
): Promise<ConfigsMap> {
  try {
    const params = new URLSearchParams({ platform });
    if (locale) params.set("locale", locale);
    const res = await fetch(
      `${API_URL}/remote-config/evaluate?${params.toString()}`,
      { cache: "no-store" },
    );
    if (!res.ok) return {};
    const data = (await res.json()) as EvaluatedConfig[];
    const map: ConfigsMap = {};
    for (const c of data) map[c.key] = { type: c.type, value: c.value };
    return map;
  } catch {
    return {};
  }
}

// ─── Typed getters ─────────────────────────────────────────────────────
// Each getter applies a runtime `typeof` guard and falls back to the
// caller-supplied default when the key is missing, null/undefined, or
// the value's actual type doesn't match the expected one. This protects
// the UI from a misconfigured entry in the dashboard breaking layouts.

/** Reads a string config; returns `defaultValue` on miss/type-mismatch. */
export function getConfigString(
  configs: ConfigsMap,
  key: string,
  defaultValue: string,
): string {
  const entry = configs[key];
  if (!entry || entry.value === null || entry.value === undefined)
    return defaultValue;
  return typeof entry.value === "string" ? entry.value : defaultValue;
}

/** Reads a finite number config; returns `defaultValue` on miss/type-mismatch. */
export function getConfigNumber(
  configs: ConfigsMap,
  key: string,
  defaultValue: number,
): number {
  const entry = configs[key];
  if (!entry || entry.value === null || entry.value === undefined)
    return defaultValue;
  return typeof entry.value === "number" && Number.isFinite(entry.value)
    ? entry.value
    : defaultValue;
}

/** Reads a boolean config; returns `defaultValue` on miss/type-mismatch. */
export function getConfigBoolean(
  configs: ConfigsMap,
  key: string,
  defaultValue: boolean,
): boolean {
  const entry = configs[key];
  if (!entry || entry.value === null || entry.value === undefined)
    return defaultValue;
  return typeof entry.value === "boolean" ? entry.value : defaultValue;
}

/**
 * Reads a JSON config typed as `T` by the caller.
 *
 * No structural validation is performed — the dashboard editor is
 * considered the trusted source. If runtime validation is needed (rare),
 * pipe the result through Zod or similar at the call site.
 */
export function getConfigJson<T = unknown>(
  configs: ConfigsMap,
  key: string,
  defaultValue: T,
): T {
  const entry = configs[key];
  if (!entry || entry.value === null || entry.value === undefined)
    return defaultValue;
  return entry.value as T;
}
